/**
 * QA Session System Types
 *
 * Type definitions for the in-app QA testing and monitoring system.
 * This system enables admins to record, track, and analyze manual testing sessions.
 */

/**
 * User type context for QA events
 */
export type QAUserType = 'individual' | 'household_owner' | 'household_member' | 'admin'

/**
 * Risk/Severity tier for QA events
 */
export type QARiskLevel = 'R0' | 'R1' | 'R2' | 'R3'

/**
 * QA Session lifecycle states
 */
export type QASessionState = 'idle' | 'recording' | 'paused' | 'completed'

/**
 * Types of events captured during QA sessions
 */
export type QAEventType =
  | 'console_error'
  | 'console_warn'
  | 'promise_rejection'
  | 'network_error'
  | 'network_timeout'
  | 'react_error'
  | 'permission_violation'
  | 'manual_flag'
  | 'manual_note'
  | 'screenshot'
  | 'route_change'
  | 'session_start'
  | 'session_pause'
  | 'session_resume'
  | 'session_end'

/**
 * Screenshot artifact captured during session
 */
export interface QAScreenshot {
  id: string
  timestamp: number
  dataUrl: string
  route: string
  userType: QAUserType
  triggeredBy: 'auto' | 'manual' | 'error'
  associatedEventId?: string
}

/**
 * Screen recording artifact captured during session
 */
export interface QARecording {
  id: string
  startTime: number
  endTime?: number
  duration?: number
  blobUrl?: string
  storagePath?: string
  route: string
  userType: QAUserType
  fileSize?: number
  mimeType?: string
}

/**
 * Individual QA event captured during session
 */
export interface QAEvent {
  id: string
  type: QAEventType
  timestamp: number
  route: string
  userType: QAUserType
  riskLevel: QARiskLevel
  message: string
  details?: {
    stackTrace?: string
    componentStack?: string
    url?: string
    method?: string
    status?: number
    duration?: number
    errorMessage?: string
    note?: string
  }
  screenshotId?: string
  flagged?: boolean
  checklistItemId?: string // Associated checklist item
}

/**
 * QA Session metadata and configuration
 */
export interface QASessionMetadata {
  sessionId: string
  startTime: number
  endTime?: number
  duration?: number
  userType: QAUserType
  accountId?: string
  accountEmail?: string
  initialRoute: string
  userAgent: string
  viewport: {
    width: number
    height: number
  }
}

/**
 * Complete QA session with all captured data
 */
export interface QASession {
  metadata: QASessionMetadata
  state: QASessionState
  events: QAEvent[]
  screenshots: QAScreenshot[]
  recordings: QARecording[]
  routesVisited: string[]
  checklist?: {
    version: string
    activeSection?: string
    activeCategory?: string
    activeItem?: string
    items: Record<string, { status: string; notes?: string; timestamp?: number }>
  }
  summary: {
    totalEvents: number
    errorsByRisk: {
      R0: number
      R1: number
      R2: number
      R3: number
    }
    eventsByType: Record<QAEventType, number>
    consoleErrors: number
    networkErrors: number
    permissionViolations: number
    manualFlags: number
  }
}

/**
 * Stored QA session (persisted to localStorage or database)
 */
export interface StoredQASession extends QASession {
  id: string
  createdAt: number
  createdBy: string
  tags?: string[]
  notes?: string
}

/**
 * QA Report generation output
 */
export interface QAReport {
  session: QASession
  generatedAt: number
  format: 'json' | 'markdown'
  content: string
}

/**
 * QA Session configuration options
 */
export interface QASessionConfig {
  autoScreenshotOnError: boolean
  autoScreenshotOnPermissionViolation: boolean
  captureConsoleErrors: boolean
  captureConsoleWarns: boolean
  captureNetworkErrors: boolean
  captureNetworkTimeouts: boolean
  networkTimeoutMs: number
  maxEventsPerSession: number
  maxScreenshotsPerSession: number
}

/**
 * Default risk levels for different event types
 */
export const DEFAULT_RISK_LEVELS: Record<QAEventType, QARiskLevel> = {
  console_error: 'R1',
  console_warn: 'R2',
  promise_rejection: 'R1',
  network_error: 'R0',
  network_timeout: 'R1',
  react_error: 'R0',
  permission_violation: 'R0',
  manual_flag: 'R1',
  manual_note: 'R3',
  screenshot: 'R3',
  route_change: 'R3',
  session_start: 'R3',
  session_pause: 'R3',
  session_resume: 'R3',
  session_end: 'R3',
}

/**
 * Default session configuration
 */
export const DEFAULT_SESSION_CONFIG: QASessionConfig = {
  autoScreenshotOnError: true,
  autoScreenshotOnPermissionViolation: true,
  captureConsoleErrors: true,
  captureConsoleWarns: true,
  captureNetworkErrors: true,
  captureNetworkTimeouts: true,
  networkTimeoutMs: 10000,
  maxEventsPerSession: 1000,
  maxScreenshotsPerSession: 50,
}
